import {
  useGetUsersSuspense,
  usePostUsers,
} from 'api/payments/generated/fragmentPaymentsAPI';
import {
  PaymentViewHeader,
  PaymentViewLayout,
  PaymentViewLoading,
} from 'components/Layout/PaymentViewLayout';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import { capitalize } from 'lodash-es';
import React, { Suspense, useCallback, useMemo, useState } from 'react';
import { makePath } from 'utils/routes';
import { useQueryClient } from '@tanstack/react-query';

import { FormButtons } from '@fragment/ui/src/components/Button/ButtonGroup/FormButtons';
import { PrimaryButtonV3 } from '@fragment/ui/src/components/Button/PrimaryButtonV3/PrimaryButtonV3';
import { ErrorText } from '@fragment/ui/src/components/ErrorText';
import { Modal } from '@fragment/ui/src/components/Modal/Modal';
import { ModalField } from '@fragment/ui/src/components/Modal/ModalField';
import { ModalTextInput } from '@fragment/ui/src/components/Modal/ModalTextInput';
import type { ColumnDefinition } from '@fragment/ui/src/components/Table';
import { NavTableV2 } from '@fragment/ui/src/components/Table/NavTableV2';
import { TabsV2 } from '@fragment/ui/src/components/Tabs/TabsV2';

import { PLATFORM_ENTITY } from './constants';

type UserRow = {
  id: string;
  externalId: string;
  role?: string;
};

const emptyValue = {
  'External ID': 'No Users Yet',
  Verified: 'N/A',
};

const schema: ColumnDefinition<UserRow>[] = [
  {
    title: 'External ID',
    colspan: 'col-span-2',
    key: 'externalId',
  },
  {
    title: 'Role',
    justify: 'right',
    format: (row: UserRow) => {
      if (row.externalId === PLATFORM_ENTITY) {
        return <span className="text-main">Platform</span>;
      }

      return (
        <span className="truncate text-main-500">
          {row.role ? capitalize(row.role) : '-'}
        </span>
      );
    },
  },
];

type Props = {
  workspaceId: string;
};

const UsersListPageContent = ({ workspaceId }: Props): JSX.Element => {
  useDocumentTitle('Users');

  const { data } = useGetUsersSuspense();
  // Note: Suspense handles loading state, error boundary handles errors
  const queryClient = useQueryClient();
  const createUserMutation = usePostUsers();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [externalId, setExternalId] = useState('');
  const [roleInput, setRoleInput] = useState('');
  const [createError, setCreateError] = useState<string | undefined>();
  const [selectedRole, setSelectedRole] = useState<string>('all');

  const users: UserRow[] = useMemo(
    () =>
      data.data.map((user) => ({
        id: user.externalId, // Using externalId as id since there's no separate id field
        externalId: user.externalId,
        role: user.role,
      })),
    [data.data]
  );

  // Extract unique roles from all users and create filter options
  const roleFilterOptions = useMemo(() => {
    const roleSet = new Set<string>();
    users.forEach((user) => {
      if (user.role) {
        roleSet.add(user.role);
      }
    });
    const roles = Array.from(roleSet).sort();

    return [
      { value: 'all' as const, label: 'All' },
      ...roles.map((role) => ({ value: role, label: capitalize(role) })),
    ];
  }, [users]);

  // Filter users by selected role
  const filteredUsers = useMemo(() => {
    if (selectedRole === 'all') {
      return users;
    }
    return users.filter((user) => user.role === selectedRole);
  }, [users, selectedRole]);

  const getPath = useCallback(
    (user: UserRow) =>
      makePath(['w', workspaceId, 'payments', 'users', user.externalId], {
        absolute: true,
      }),
    [workspaceId]
  );

  const handleCreateUser = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setCreateError(undefined);
      try {
        await createUserMutation.mutateAsync({
          data: {
            externalId,
            role: roleInput.trim(),
          },
        });

        // Refetch the users list
        await queryClient.invalidateQueries({ queryKey: ['/users'] });

        // Close modal and reset form
        setShowCreateModal(false);
        setExternalId('');
        setRoleInput('');
      } catch (err) {
        setCreateError(
          err instanceof Error ? err.message : 'Failed to create user'
        );
      }
    },
    [externalId, roleInput, createUserMutation, queryClient]
  );

  const handleCloseModal = useCallback(() => {
    setShowCreateModal(false);
    setExternalId('');
    setRoleInput('');
    setCreateError(undefined);
  }, []);

  return (
    <PaymentViewLayout header={<PaymentViewHeader>Users</PaymentViewHeader>}>
      <div className="flex flex-col min-h-f1 w-full h-full">
        <div className="flex flex-row justify-between">
          <TabsV2
            options={roleFilterOptions}
            selectedValue={selectedRole}
            onClick={setSelectedRole}
          />
          <div>
            <PrimaryButtonV3
              data-testid="create-user-button"
              onClick={() => setShowCreateModal(true)}
            >
              +
            </PrimaryButtonV3>
          </div>
        </div>
        <NavTableV2
          textSize="md"
          data={filteredUsers}
          emptyValue={emptyValue}
          getPath={getPath}
          schema={schema}
          showHeader={false}
        />
        {showCreateModal && (
          <Modal
            onClose={handleCloseModal}
            header="Create User"
            onSubmit={handleCreateUser}
            body={
              <div className="flex flex-col space-y-f2">
                <ModalField
                  label={<label htmlFor="external-id">External ID</label>}
                >
                  <ModalTextInput
                    id="external-id"
                    data-testid="user-external-id-input"
                    value={externalId}
                    onChange={(e) => setExternalId(e.target.value)}
                    placeholder="Enter external ID"
                  />
                </ModalField>
                <ModalField label={<label htmlFor="role">Role</label>}>
                  <ModalTextInput
                    id="role"
                    data-testid="user-role-input"
                    value={roleInput}
                    onChange={(e) => setRoleInput(e.target.value)}
                    placeholder="e.g., buyer, seller"
                    required
                  />
                </ModalField>
              </div>
            }
            footer={
              <div className="flex flex-col gap-y-f2 w-full">
                {createError && <ErrorText>{createError}</ErrorText>}
                <div className="flex flex-row justify-between">
                  <FormButtons
                    type="secondary"
                    cancelButtonLabel="Cancel"
                    onCancel={handleCloseModal}
                    submitButtonLabel={
                      createUserMutation.isPending ? 'Creating...' : 'Create'
                    }
                    submitButtonTestId="user-submit-button"
                    disabled={
                      createUserMutation.isPending ||
                      !externalId ||
                      !roleInput.trim()
                    }
                  />
                </div>
              </div>
            }
          />
        )}
      </div>
    </PaymentViewLayout>
  );
};

export const UsersListPage = ({ workspaceId }: Props): JSX.Element => (
  <Suspense fallback={<PaymentViewLoading message="Loading users..." />}>
    <UsersListPageContent workspaceId={workspaceId} />
  </Suspense>
);
